import { z } from 'zod';
import { LIMITS } from '../limits.js';

/**
 * Follow-ups ("Ответить позже") — a personal, private reminder to come back to
 * a specific message. The owner is the only one who ever sees their follow-ups.
 *
 * Wire shapes only; the server owns channel resolution + visibility checks. The
 * client never sends a channelId — the server derives it from the message so a
 * forged channelId can't smuggle a follow-up onto a message you can't see.
 */

/** Lifecycle. `snoozed`/`scheduled`/`overdue` are NOT stored states — they are
 *  derived from `remindAt` vs. now. Only these two are persisted. */
export const followUpStatusSchema = z.enum(['pending', 'done']);
export type FollowUpStatus = z.infer<typeof followUpStatusSchema>;

/** ISO-8601 instant, or null = "no time, just keep it in my inbox". */
const remindAtSchema = z.string().datetime({ offset: true }).nullable();

export const createFollowUpSchema = z.object({
  messageId: z.string().cuid(),
  remindAt: remindAtSchema.optional(),
  note: z.string().max(LIMITS.followUp.noteMax).optional(),
});
export type CreateFollowUpInput = z.infer<typeof createFollowUpSchema>;

/** Partial update — drives snooze (`remindAt`), done/reopen (`status`) and an
 *  optional editable note. At least one field must be present. */
export const updateFollowUpSchema = z
  .object({
    remindAt: remindAtSchema.optional(),
    status: followUpStatusSchema.optional(),
    note: z.string().max(LIMITS.followUp.noteMax).nullable().optional(),
  })
  .refine((v) => v.remindAt !== undefined || v.status !== undefined || v.note !== undefined, {
    message: 'empty_update',
  });
export type UpdateFollowUpInput = z.infer<typeof updateFollowUpSchema>;
